import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { projectAPI } from '../../services/api';
import { formatMarathiNumber, toMarathiDigits } from '../../utils/marathi';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import './ProjectsPage.css';

const CATEGORY_META = {
  road: { label: 'रस्ता', icon: '▰', color: '#2f80ed', bg: '#eaf3ff' },
  water: { label: 'पाणीपुरवठा', icon: '💧', color: '#23a8e0', bg: '#e8f7ff' },
  water_supply: { label: 'पाणीपुरवठा', icon: '💧', color: '#23a8e0', bg: '#e8f7ff' },
  drainage: { label: 'सांडपाणी', icon: '≋', color: '#8b5cf6', bg: '#f1edff' },
  building: { label: 'इमारत', icon: '▥', color: '#9b51e0', bg: '#f3eafe' },
  electrification: { label: 'विद्युतीकरण', icon: '⚡', color: '#f59e0b', bg: '#fff4dc' },
  electricity: { label: 'विद्युतीकरण', icon: '⚡', color: '#f59e0b', bg: '#fff4dc' },
  sanitation: { label: 'स्वच्छता', icon: '♻', color: '#10b981', bg: '#e8f8ef' },
  education: { label: 'शिक्षण', icon: '▣', color: '#2563eb', bg: '#edf4ff' },
  health: { label: 'आरोग्य', icon: '✚', color: '#ef476f', bg: '#fff0f4' },
  agriculture: { label: 'कृषी', icon: '♣', color: '#22a447', bg: '#eaf8ef' },
  housing: { label: 'घरकुल', icon: '⌂', color: '#f97316', bg: '#fff0e5' },
  digital: { label: 'डिजिटल', icon: '▤', color: '#0ea5e9', bg: '#e9f7ff' },
  community: { label: 'सामुदायिक', icon: '👥', color: '#8b5cf6', bg: '#f1edff' },
  other: { label: 'इतर', icon: '●', color: '#64748b', bg: '#eef2f6' },
};

const STATUS_META = {
  proposed: { label: 'नवीन', icon: '●', color: '#f59e0b', bg: '#fff4dc' },
  approved: { label: 'मंजूर', icon: '⌂', color: '#2f80ed', bg: '#eaf3ff' },
  in_progress: { label: 'प्रगतीत', icon: '▰', color: '#f59e0b', bg: '#fff4dc' },
  completed: { label: 'पूर्ण', icon: '✓', color: '#16a34a', bg: '#e8f8ef' },
  on_hold: { label: 'स्थगित', icon: 'Ⅱ', color: '#8b5cf6', bg: '#f1edff' },
  cancelled: { label: 'रद्द', icon: '×', color: '#e11d48', bg: '#fff0f4' },
};

const STATUS_ORDER = ['proposed', 'approved', 'in_progress', 'completed', 'on_hold', 'cancelled'];

const formatMoney = value => {
  const number = Number(value || 0);
  return `₹ ${formatMarathiNumber(number)}`;
};

const formatDate = value => value ? toMarathiDigits(new Date(value).toLocaleDateString('mr-IN', { day: 'numeric', month: 'short', year: 'numeric' })) : '—';

const getCategory = category => CATEGORY_META[category] || CATEGORY_META.other;
const getStatus = status => STATUS_META[status] || STATUS_META.proposed;

export default function ProjectsPage() {
  const { tenantSlug } = useParams();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');

  useEffect(() => {
    setLoading(true);
    projectAPI.getAll(tenantSlug)
      .then(res => setProjects(res.data.projects || []))
      .catch(() => setError('विकास कामांची माहिती लोड करता आली नाही.'))
      .finally(() => setLoading(false));
  }, [tenantSlug]);

  const categories = useMemo(() => [...new Set(projects.map(project => project.category).filter(Boolean))], [projects]);
  const filtered = useMemo(() => projects.filter(project => {
    const query = search.trim().toLowerCase();
    const queryOk = !query || [project.title, project.description, project.funding_source, project.category].filter(Boolean).some(value => String(value).toLowerCase().includes(query));
    const statusOk = statusFilter === 'all' || project.status === statusFilter;
    const categoryOk = categoryFilter === 'all' || project.category === categoryFilter;
    return queryOk && statusOk && categoryOk;
  }), [projects, search, statusFilter, categoryFilter]);

  const totalBudget = projects.reduce((sum, project) => sum + Number(project.estimated_cost || 0), 0);
  const spentBudget = projects.reduce((sum, project) => sum + Number(project.actual_cost || 0), 0);
  const completed = projects.filter(project => project.status === 'completed').length;
  const inProgress = projects.filter(project => project.status === 'in_progress').length;
  const proposed = projects.filter(project => project.status === 'proposed').length;
  const avgProgress = projects.length ? Math.round(projects.reduce((sum, project) => sum + Number(project.progress_percentage || 0), 0) / projects.length) : 0;

  const categoryTotals = categories.map(category => ({
    category,
    meta: getCategory(category),
    count: projects.filter(project => project.category === category).length,
    total: projects.filter(project => project.category === category).reduce((sum, project) => sum + Number(project.estimated_cost || 0), 0),
  }));

  let donutCursor = 0;
  const donutSegments = categoryTotals.map(item => {
    const percent = totalBudget ? (item.total / totalBudget) * 100 : 0;
    const segment = `${item.meta.color} ${donutCursor}% ${donutCursor + percent}%`;
    donutCursor += percent;
    return segment;
  }).join(', ');

  const stats = [
    { icon: '▤', label: 'एकूण कामे', value: projects.length, sub: 'सर्व कामे', tone: 'blue' },
    { icon: '✓', label: 'पूर्ण झालेली', value: completed, sub: `${formatMarathiNumber(projects.length ? Math.round(completed / projects.length * 100) : 0)}%`, tone: 'green' },
    { icon: '⌂', label: 'प्रगतीत', value: inProgress, sub: 'काम सुरू आहे', tone: 'orange' },
    { icon: '₹', label: 'एकूण खर्च', value: formatMoney(totalBudget), sub: 'अंदाजित खर्च', tone: 'purple' },
  ];

  const notices = [
    { label: `नवीन कामे: ${formatMarathiNumber(proposed)}`, active: proposed > 0 },
    { label: `मंजूर कामे: ${formatMarathiNumber(projects.filter(project => project.status === 'approved').length)}`, active: projects.some(project => project.status === 'approved') },
    { label: `प्रगतीत कामे: ${formatMarathiNumber(inProgress)}`, active: inProgress > 0 },
  ];

  return (
    <div className="projects-public-page">
      <div className="projects-breadcrumb"><Link to={`/gp/${tenantSlug}`}>मुख्यपृष्ठ</Link><span>›</span><strong>विकास कामे</strong><span>›</span><strong>कामांची यादी</strong></div>

      <div className="projects-layout">
        <aside className="projects-left-rail">
          <section className="projects-side-card">
            <h2>विकास कामे</h2>
            <div className="projects-side-filters">
              <button className={statusFilter === 'all' ? 'active' : ''} onClick={() => setStatusFilter('all')}>सर्व कामे <span>{formatMarathiNumber(projects.length)}</span></button>
              {STATUS_ORDER.filter(status => projects.some(project => project.status === status)).map(status => (
                <button key={status} className={statusFilter === status ? 'active' : ''} onClick={() => setStatusFilter(status)}>
                  {getStatus(status).label} <span>{formatMarathiNumber(projects.filter(project => project.status === status).length)}</span>
                </button>
              ))}
            </div>
          </section>

          <section className="projects-help-card">
            <span>☊</span>
            <h2>मदत हवी आहे?</h2>
            <p>विकास कामांबाबत माहिती किंवा तक्रार नोंदवण्यासाठी संपर्क साधा.</p>
            <Link to={`/gp/${tenantSlug}/contact`}>संपर्क साधा</Link>
          </section>
        </aside>

        <main className="projects-main">
          <section className="projects-hero-card">
            <div>
              <span>ग्रामपंचायत विकास कामे</span>
              <h1>विकास कामांची यादी</h1>
              <p>ग्रामपंचायत हद्दीतील विकास कामांची माहिती, स्थिती, खर्च आणि प्रगती.</p>
            </div>
          </section>

          <section className="projects-stats-grid">
            {stats.map(stat => (
              <article key={stat.label} className={`project-stat ${stat.tone}`}>
                <span>{stat.icon}</span>
                <p><strong>{typeof stat.value === 'number' ? formatMarathiNumber(stat.value) : stat.value}</strong><small>{stat.label}</small><em>{stat.sub}</em></p>
              </article>
            ))}
          </section>

          <section className="projects-toolbar">
            <select value={categoryFilter} onChange={event => setCategoryFilter(event.target.value)}>
              <option value="all">सर्व विभाग</option>
              {categories.map(category => <option key={category} value={category}>{getCategory(category).label}</option>)}
            </select>
            <select value={statusFilter} onChange={event => setStatusFilter(event.target.value)}>
              <option value="all">सर्व स्थिती</option>
              {STATUS_ORDER.filter(status => projects.some(project => project.status === status)).map(status => <option key={status} value={status}>{getStatus(status).label}</option>)}
            </select>
            <div><input value={search} onChange={event => setSearch(event.target.value)} placeholder="कामाचे नाव शोधा..." /><span>⌕</span></div>
          </section>

          <section className="projects-table-card">
            {loading ? (
              <div className="projects-state">विकास कामे लोड होत आहेत...</div>
            ) : error ? (
              <div className="projects-state error">{error}</div>
            ) : filtered.length === 0 ? (
              <div className="projects-state">निवडलेल्या फिल्टरसाठी कोणतीही कामे उपलब्ध नाहीत.</div>
            ) : (
              <>
                <div className="projects-table-wrap">
                <table className="projects-table">
                  <thead>
                    <tr>
                      <th>कामाचे नाव</th>
                      <th>विभाग</th>
                      <th>एकूण खर्च</th>
                      <th>प्रगती</th>
                      <th>स्थिती</th>
                      <th>तपशील</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <LoadingSpinner.TableRow colSpan={6} message="विकासकामांची माहिती लोड होत आहे..." />
                    ) : filtered.length === 0 ? (
                      <tr><td colSpan={6} style={{ textAlign: 'center', padding: '36px 16px', color: '#64748b' }}>कोणतीही विकास कामे आढळली नाहीत.</td></tr>
                    ) : filtered.map(project => {
                      const category = getCategory(project.category);
                      const status = getStatus(project.status);
                      const progress = Number(project.progress_percentage || 0);
                      const progressColor = project.status === 'completed' ? '#16a34a' : category.color;
                      return (
                        <tr key={project.id}>
                          <td>
                            <div className="project-name-cell" style={{ '--cat-color': category.color, '--cat-bg': category.bg }}>
                              <span>{category.icon}</span>
                              <p><strong>{project.title}</strong><small>दिनांक: {formatDate(project.start_date || project.created_at || project.createdAt)}</small></p>
                            </div>
                          </td>
                          <td><b className="project-category" style={{ '--cat-color': category.color, '--cat-bg': category.bg }}>{category.label}</b></td>
                          <td className="project-money">{formatMoney(project.estimated_cost)}</td>
                          <td>
                            <div className="project-progress" style={{ '--progress-color': progressColor }}>
                              <i><b style={{ width: `${Math.min(progress, 100)}%` }} /></i><span>{formatMarathiNumber(progress)}%</span>
                            </div>
                          </td>
                          <td><span className="project-status" style={{ '--status-color': status.color, '--status-bg': status.bg }}>{status.label}</span></td>
                          <td><Link className="project-detail-link" to={`/gp/${tenantSlug}/projects/${project.id}`}>तपशील</Link></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>
                <div className="projects-mobile-list">
                {loading ? (
                  <LoadingSpinner message="विकासकामांची माहिती लोड होत आहे..." />
                ) : filtered.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '36px 16px', color: '#64748b' }}>कोणतीही विकास कामे आढळली नाहीत.</div>
                ) : filtered.map(project => {
                  const category = getCategory(project.category);
                  const status = getStatus(project.status);
                  const progress = Number(project.progress_percentage || 0);
                  const progressColor = project.status === 'completed' ? '#16a34a' : category.color;
                  return (
                    <article className="project-mobile-card" key={project.id} style={{ '--cat-color': category.color, '--cat-bg': category.bg }}>
                      <div className="project-mobile-heading">
                        <span className="project-mobile-icon">{category.icon}</span>
                        <div>
                          <h3>{project.title}</h3>
                          <small>दिनांक: {formatDate(project.start_date || project.created_at || project.createdAt)}</small>
                        </div>
                        <b className="project-category">{category.label}</b>
                      </div>
                      <div className="project-mobile-meta">
                        <p><small>एकूण खर्च</small><strong>{formatMoney(project.estimated_cost)}</strong></p>
                        <p><small>स्थिती</small><span className="project-status" style={{ '--status-color': status.color, '--status-bg': status.bg }}>{status.label}</span></p>
                      </div>
                      <div className="project-mobile-progress">
                        <div><small>प्रगती</small><strong>{formatMarathiNumber(progress)}%</strong></div>
                        <i style={{ '--progress-color': progressColor }}><b style={{ width: `${Math.min(progress, 100)}%` }} /></i>
                      </div>
                      <Link className="project-detail-link" to={`/gp/${tenantSlug}/projects/${project.id}`}>तपशील पहा →</Link>
                    </article>
                  );
                })}
                </div>
              </>
            )}
          </section>
        </main>

        <aside className="projects-right-rail">
          <section className="projects-doc-card">
            <h2>कामांचा आढावा</h2>
            <div><span>▧</span><p><strong>{formatMarathiNumber(projects.length)} कामे नोंदवलेली</strong><small>सर्व विकास कामे</small></p><b>{formatMarathiNumber(avgProgress)}%</b></div>
            <div><span>✓</span><p><strong>{formatMarathiNumber(completed)} कामे पूर्ण</strong><small>पूर्ण झालेली कामे</small></p><b>{formatMarathiNumber(projects.length ? Math.round((completed / projects.length) * 100) : 0)}%</b></div>
            <div><span>•</span><p><strong>{formatMarathiNumber(inProgress)} कामे प्रगतीत</strong><small>सध्या सुरू असलेली कामे</small></p><b>{formatMoney(spentBudget)}</b></div>
            <Link to={`/gp/${tenantSlug}/contact`}>कामाबाबत चौकशी करा</Link>
          </section>

          <section className="projects-chart-card">
            <h2>बजेटचा आढावा</h2>
            <div className="budget-ring" style={{ background: donutSegments ? `conic-gradient(${donutSegments})` : undefined }}><span>{formatMarathiNumber(avgProgress)}%</span></div>
            <p><strong>एकूण बजेट:</strong> {formatMoney(totalBudget)}</p>
            <p><strong>खर्च झाले:</strong> {formatMoney(spentBudget)}</p>
            <div className="category-legend">
              {categoryTotals.map(item => (
                <div className="category-row" key={item.category}><span><i style={{ background: item.meta.color }} />{item.meta.label}</span><b>{formatMarathiNumber(item.count)}</b></div>
              ))}
            </div>
          </section>

          <section className="projects-notice-card">
            <h2>सूचना</h2>
            {notices.map(item => <p key={item.label} className={item.active ? 'active' : ''}><span />{item.label}</p>)}
          </section>
        </aside>
      </div>
    </div>
  );
}
